import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AdminShell } from "./AdminShell";
import { PageHeader, StatusBadge, StatusMenu } from "./parts";

const EMPTY = { name: "", city: "", postal_code: "", address: "" };

function SchoolDialog({ open, onOpenChange, school, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(school ? { name: school.name || "", city: school.city || "", postal_code: school.postal_code || "", address: school.address || "" } : EMPTY);
  }, [school, open]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Le nom de l'école est obligatoire.");
    setSaving(true);
    try {
      if (school) await api.patch(`/admin/schools/${school.id}`, form);
      else await api.post("/admin/schools", form);
      toast.success(school ? "École modifiée." : "École créée.");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="school-dialog">
        <DialogHeader>
          <DialogTitle>{school ? "Modifier l'école" : "Nouvelle école"}</DialogTitle>
          <DialogDescription>Renseignez les informations de l'établissement.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Nom de l'école *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Ville</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Code postal</Label>
              <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Adresse</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="school-save-btn">
            {saving && <Loader2 className="mr-2 animate-spin" size={16} />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSchools() {
  const [schools, setSchools] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/schools");
      setSchools(res.data);
    } catch {
      toast.error("Impossible de charger les écoles.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (s, status) => {
    try {
      await api.post(`/admin/schools/${s.id}/status`, { status });
      toast.success(status === "supprimé" ? "École supprimée (restaurable 30 jours)." : "Statut mis à jour.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminShell>
      <PageHeader
        title="Écoles"
        subtitle="Gérez les établissements de la plateforme."
        action={
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-school-btn">
            <Plus size={16} className="mr-1.5" /> Ajouter une école
          </Button>
        }
      />
      {schools === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)]" data-testid="schools-table">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">École</th>
                <th className="px-5 py-3">Ville</th>
                <th className="px-5 py-3 text-center">Enseignants</th>
                <th className="px-5 py-3 text-center">Élèves</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => (
                <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/40" data-testid={`school-row-${s.id}`}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <Building2 size={15} className="text-slate-400" />
                      {s.name}
                      {s.is_default && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">par défaut</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">{s.city || "—"}</td>
                  <td className="px-5 py-3.5 text-center text-slate-600">{s.teacher_count}</td>
                  <td className="px-5 py-3.5 text-center text-slate-600">{s.student_count}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={s.status} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => { setEditing(s); setDialogOpen(true); }}
                        className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                        data-testid={`edit-school-${s.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      {!s.is_default && (
                        <StatusMenu current={s.status} onChange={(st) => changeStatus(s, st)} testid={`school-menu-${s.id}`} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <SchoolDialog open={dialogOpen} onOpenChange={setDialogOpen} school={editing} onSaved={load} />
    </AdminShell>
  );
}
